import { Button } from '@strata/react-buttons';
import { WarningDialog } from '@strata/react-dialog';
import { FormContainer } from '@strata/react-form-container';
import { Grid } from '@strata/react-grid';
import { TextInputFormik } from '@strata/react-inputs';
import { SuccessToast } from '@strata/react-toast-message';
import { dialogStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import * as Yup from 'yup';

import { AsOfDateField } from 'pages/therapyGroups/AsOfDateField';
import { AddProductDialog } from 'pages/therapyGroups/dialogs/AddProductDialog';
import { EditClientPreferenceDialog } from 'pages/therapyGroups/dialogs/EditClientPreferenceDialog';
import { RemoveParentProductDialog } from 'pages/therapyGroups/dialogs/RemoveParentProductDialog';
import { therapyGroupOidColumns } from 'pages/therapyGroups/TherapyGroupOidColumns';
import { useTherapyGroupAsOf } from 'pages/therapyGroups/useTherapyGroupAsOf';
import { CREATE_ROUTE } from 'services/constants';
import { handleError } from 'services/ErrorManagement';
import { useGet } from 'services/Http';
import {
    removeParentProductLink,
    saveClientPreferenceChanges,
    saveParentProductLink,
    saveTherapyGroup,
    saveTherapyGroupRecord,
    therapyGroupAppPath,
    therapyGroupHasPendingChanges,
    TherapyGroupsAppRoute,
} from 'services/TherapyGroupHelpers';
import {
    getPreferenceChanges,
    getTherapyGroupOidRows,
    removeOidsFromPreferences,
} from 'services/TherapyGroupPreferenceHelpers';
import {
    descriptionSchema,
    DisplayNameHelperText,
    requireTherapyGroupIdentity,
    therapyGroupNameSchema,
} from 'services/TherapyGroupValidation';

export const TherapyGroupDetails = () => {
    const { therapyGroupName } = useParams();
    const navigate = useNavigate();
    const goHome = () => navigate(TherapyGroupsAppRoute);
    const isCreate = therapyGroupName === CREATE_ROUTE;

    const {
        asOf,
        therapyGroup,
        todayLinkNames,
        viewedLinkNames,
        todayPreferences,
        viewedPreferences,
        windows,
        viewedWindows,
        windowsLoaded,
        linksLoaded,
        refetch,
    } = useTherapyGroupAsOf(therapyGroupName);
    const parentProducts = useGet('/reference/parent_product_services');
    const payerSegments = useGet('/reference/payer_segments');

    const [showWarning, setShowWarning] = useState(false);
    const [isAddOpen, setIsAddOpen] = useState(false);
    const [editingOid, setEditingOid] = useState(null);
    const [parentToRemove, setParentToRemove] = useState();
    const [linkNamesAfterPartialSave, setLinkNamesAfterPartialSave] = useState();
    const [preferencesAfterPartialSave, setPreferencesAfterPartialSave] = useState();

    const loadedLinkNames = todayLinkNames;
    const loadedPreferences = todayPreferences;
    const currentLinkNames = linkNamesAfterPartialSave ?? loadedLinkNames;
    const baselinePreferences = preferencesAfterPartialSave ?? loadedPreferences;

    const groupNameForWrite = async (values) => {
        if (!isCreate) {
            return therapyGroupName;
        }
        await requireTherapyGroupIdentity(values);
        await saveTherapyGroupRecord(values.therapyGroupName, values.description);
        return values.therapyGroupName;
    };

    const rememberLinks = (formik, nextLinks) => {
        formik.setFieldValue('parentProducts', nextLinks);
        setLinkNamesAfterPartialSave(nextLinks);
    };

    const rememberPreferences = (formik, nextPreferences) => {
        formik.setFieldValue('preferences', nextPreferences);
        setPreferencesAfterPartialSave(nextPreferences);
    };

    const openSavedGroup = (groupName) => {
        if (isCreate) {
            navigate(therapyGroupAppPath(groupName), { replace: true });
        }
    };

    const handleAddParent = async (formik, parentProductService) => {
        const groupName = await groupNameForWrite(formik.values);
        await saveParentProductLink(groupName, parentProductService);
        rememberLinks(formik, [...formik.values.parentProducts, parentProductService]);
        refetch();
        SuccessToast({ message: 'Product added.' });
        openSavedGroup(groupName);
    };

    const persistPreferences = async (formik, groupName, changes) => {
        try {
            const persisted = await saveClientPreferenceChanges(
                groupName,
                baselinePreferences,
                changes,
            );
            rememberPreferences(formik, persisted);
        } catch (err) {
            if (err.persistedPreferences) {
                rememberPreferences(formik, err.persistedPreferences);
            }
            throw err;
        }
    };

    // A saved window or link may only show on another as-of day, so reload both lists.
    const handleApplyPreferences = async (formik, changes) => {
        const groupName = await groupNameForWrite(formik.values);
        await persistPreferences(formik, groupName, changes);
        refetch();
        SuccessToast({ message: 'Client preference saved.' });
        openSavedGroup(groupName);
    };

    // Preferences go first so a retry after a failed unlink finds nothing left to delete.
    const handleRemoveParent = async (formik, { parentProductService, oids }) => {
        const next = removeOidsFromPreferences(baselinePreferences, oids);
        await persistPreferences(
            formik,
            therapyGroupName,
            getPreferenceChanges(baselinePreferences, next),
        );
        if (parentProductService) {
            await removeParentProductLink(therapyGroupName, parentProductService);
            rememberLinks(
                formik,
                currentLinkNames.filter((name) => name !== parentProductService),
            );
        }
        refetch();
        SuccessToast({ message: 'Product removed.' });
    };

    return (
        <Formik
            initialValues={{
                therapyGroupName: isCreate ? '' : (therapyGroup?.therapy_group_name ?? ''),
                description: therapyGroup?.description ?? '',
                parentProducts: loadedLinkNames,
                preferences: loadedPreferences,
            }}
            enableReinitialize
            validationSchema={Yup.object({
                therapyGroupName: therapyGroupNameSchema,
                description: descriptionSchema,
                parentProducts: Yup.array().min(1),
                preferences: Yup.object({
                    global: Yup.string().nullable().required(),
                }),
            })}
            onSubmit={async (values) => {
                try {
                    await saveTherapyGroup({
                        therapyGroupName: values.therapyGroupName,
                        description: values.description,
                        selectedParentProducts: values.parentProducts,
                        currentLinkNames,
                        initialPreferences: baselinePreferences,
                        preferences: values.preferences,
                    });
                    goHome();
                } catch (err) {
                    if (err.persistedLinkNames) {
                        setLinkNamesAfterPartialSave(err.persistedLinkNames);
                    }
                    if (err.persistedPreferences) {
                        setPreferencesAfterPartialSave(err.persistedPreferences);
                    }
                    handleError(`Unable to ${isCreate ? 'create' : 'update'} therapy group.`, err);
                }
            }}
        >
            {(formik) => {
                // Today shows the form's working copy, so a save updates the grid before the
                // refetch lands. Any other day is a preview of the loaded windows and links.
                const rows = getTherapyGroupOidRows({
                    parentNames: asOf.isViewingToday
                        ? formik.values.parentProducts
                        : viewedLinkNames,
                    parentProducts,
                    preferences: asOf.isViewingToday
                        ? formik.values.preferences
                        : viewedPreferences,
                });
                const pendingChanges = therapyGroupHasPendingChanges({
                    values: formik.values,
                    initialValues: formik.initialValues,
                    savedLinkNames: currentLinkNames,
                    savedPreferences: baselinePreferences,
                });
                const canSave = pendingChanges && formik.isValid;
                return (
                    <>
                        <WarningDialog
                            dialogTitle={dialogStrings.unsavedChanges.title}
                            isOpen={showWarning}
                            onSecondaryButtonClick={() => setShowWarning(false)}
                            onPrimaryButtonClick={goHome}
                            dialogContent={<p>{dialogStrings.unsavedChanges.body}</p>}
                        />
                        <RemoveParentProductDialog
                            parentToRemove={parentToRemove}
                            onClose={() => setParentToRemove(undefined)}
                            onConfirm={() => handleRemoveParent(formik, parentToRemove)}
                        />
                        <AddProductDialog
                            isOpen={isAddOpen}
                            parentProducts={parentProducts}
                            linkedParentNames={formik.values.parentProducts}
                            onAdd={(name) => handleAddParent(formik, name)}
                            onClose={() => setIsAddOpen(false)}
                        />
                        {editingOid && (
                            <EditClientPreferenceDialog
                                isOpen
                                oid={editingOid}
                                parentProducts={parentProducts}
                                preferences={formik.values.preferences}
                                payerSegments={payerSegments}
                                windows={windows}
                                onApply={(changes) => handleApplyPreferences(formik, changes)}
                                onClose={() => setEditingOid(null)}
                            />
                        )}
                        <FormContainer
                            formTitle="Therapy List Configuration"
                            isLoading={
                                !parentProducts ||
                                (!isCreate && (!therapyGroup || !windowsLoaded || !linksLoaded))
                            }
                            onFormSubmit={formik.handleSubmit}
                            isSubmitting={formik.isSubmitting || !canSave}
                            onSecondaryButtonClick={() => {
                                if (pendingChanges) {
                                    setShowWarning(true);
                                    return;
                                }
                                goHome();
                            }}
                            formLeftContent={
                                <>
                                    <TextInputFormik
                                        label="Therapy Group Identifier"
                                        id="therapyGroupName"
                                        disabled={!isCreate}
                                        required
                                    />
                                    <TextInputFormik
                                        label="Therapy Group Name"
                                        id="description"
                                        helperText={DisplayNameHelperText}
                                        required
                                    />
                                    <div className="sds_therapy-group__products-header margin__bottom--medium">
                                        <div>
                                            {!isCreate && (
                                                <AsOfDateField
                                                    day={asOf.day}
                                                    onChange={asOf.setDay}
                                                />
                                            )}
                                        </div>
                                        <Button
                                            label="Add Products"
                                            intent={Button.Intent.PRIMARY}
                                            onClick={() => setIsAddOpen(true)}
                                        />
                                    </div>
                                    <div className="sds_therapy-group__products">
                                        <Grid
                                            data={rows}
                                            columns={therapyGroupOidColumns({
                                                onEdit: setEditingOid,
                                                onRemove: setParentToRemove,
                                                preferences: baselinePreferences,
                                                parentProducts,
                                                windows: viewedWindows,
                                            })}
                                        />
                                    </div>
                                </>
                            }
                        />
                    </>
                );
            }}
        </Formik>
    );
};
